import { k8sRequest, makeK8sCreator } from "../api";
import { customLabels } from "../filters";
import { buildLabelPatch, LabelEdit } from "./labels";
import { formatAge, formatMemory } from "../format";
import { ContainerNodeRow, K8sNode, K8sNodeList, NodeDetail, NodeStatus } from "./types";

/**
 * The Ready condition drives the status column, matching `kubectl get nodes`.
 * Edge nodes that have stopped reporting sit at "Unknown" rather than
 * "NotReady", so all three states are meaningful here.
 */
export const nodeStatus = (node: K8sNode): NodeStatus => {
    const conditions = (node.status && node.status.conditions) || [];
    const ready = conditions.find((condition) => condition.type === "Ready");

    if (!ready) {
        return "Unknown";
    }
    if (ready.status === "True") {
        return "Ready";
    }
    if (ready.status === "False") {
        return "NotReady";
    }

    return "Unknown";
};

const internalIp = (node: K8sNode): string => {
    const addresses = (node.status && node.status.addresses) || [];
    const internal = addresses.find((address) => address.type === "InternalIP");

    return internal ? internal.address : "";
};

const toRow = (node: K8sNode): ContainerNodeRow => {
    const status = node.status || {};
    const info = status.nodeInfo || {};
    const capacity = status.capacity || {};

    return {
        // The grid keys rows by id; a node's name is unique within the cluster.
        id: node.metadata.name,
        name: node.metadata.name,
        status: nodeStatus(node),
        internalIp: internalIp(node),
        osImage: info.osImage || "",
        architecture: info.architecture || "",
        kubeletVersion: info.kubeletVersion || "",
        containerRuntime: info.containerRuntimeVersion || "",
        cpu: capacity.cpu || "",
        memory: formatMemory(capacity.memory),
        created: node.metadata.creationTimestamp || "",
        labels: customLabels(node.metadata.labels),
    };
};

/** Flatten a Kubernetes NodeList response into grid rows. */
export const parseNodeList = (body: unknown): ContainerNodeRow[] => {
    const list = (body || {}) as K8sNodeList;

    return (list.items || []).map(toRow);
};

/** Flatten a single Node into everything the detail Overview renders. */
export const parseNode = (body: unknown): NodeDetail | undefined => {
    const node = body as K8sNode | undefined;

    if (!node || !node.metadata || !node.metadata.name) {
        return undefined;
    }

    const status = node.status || {};

    return {
        name: node.metadata.name,
        status: nodeStatus(node),
        created: node.metadata.creationTimestamp || "",
        age: formatAge(node.metadata.creationTimestamp),
        addresses: status.addresses || [],
        conditions: status.conditions || [],
        labels: node.metadata.labels || {},
        info: status.nodeInfo || {},
        capacity: status.capacity || {},
        allocatable: status.allocatable || {},
    };
};

const list = makeK8sCreator<ContainerNodeRow[]>("k8sNodes", parseNodeList);
const labelPatch = makeK8sCreator<NodeDetail | undefined>("k8sNodeLabels", parseNode);
const detail = makeK8sCreator<NodeDetail | undefined>("k8sNode", parseNode);

const getAction = () => list.createThunk(k8sRequest("api/v1/nodes"));

const getNodeAction = (name: string) => detail.createThunk(k8sRequest(`api/v1/nodes/${encodeURIComponent(name)}`));

/**
 * Set or remove labels on one node.
 *
 * A strategic merge patch, which is what `kubectl label` sends: a key set to
 * null is removed. Bulk edits are this call repeated per node - Kubernetes has
 * no endpoint for labelling a set of nodes in one request.
 */
export const STRATEGIC_MERGE_PATCH = "application/strategic-merge-patch+json";

/** The request options for a label patch, separated out so they can be asserted. */
export const nodeLabelsRequest = (name: string, edit: LabelEdit) => ({
    ...k8sRequest(`api/v1/nodes/${encodeURIComponent(name)}`),
    method: "PATCH" as const,
    // Must be `type`, not a Content-Type header: source/script/ajax.ts calls
    // req.type() *after* .set(headers), so a header is always overwritten by
    // the default application/json - which the API server answers with 415.
    type: STRATEGIC_MERGE_PATCH,
    data: buildLabelPatch(edit),
});

const patchNodeLabelsAction = (name: string, edit: LabelEdit) => labelPatch.createThunk(nodeLabelsRequest(name, edit));

export { getAction, getNodeAction, patchNodeLabelsAction };
export const reducer = list.reducer;
export const nodeDetailReducer = detail.reducer;
export const nodeLabelsReducer = labelPatch.reducer;
